/**
 * PageLayout - Unified page layout wrapper for consistent spacing and structure.
 *
 * THE PAGE HEADER ZONE (locked standard — see designSystem.ts `pageStructure`):
 * Every page opens with ONE fixed-order header zone of pluggable slots. The zone
 * carries the page's opening rhythm so individual pages never rebuild it locally.
 *
 *   ① TITLE           — `title` prop      → .page-header h1            ALWAYS
 *   ② SUB-HEADING     — `description`     → .page-header p             OPTIONAL
 *   ③ INTRO GUIDE     — `intro` slot      → <PageIntroGuide> always-open OPTIONAL
 *   ④ HEADER HAIRLINE — .page-header-rule (a flat --hairline divider)  ALWAYS
 *   ⑤ TAB BAR         — `tabs` slot       → page tabs (TabsList)       OPTIONAL
 *   → then `children`: SectionMarker eyebrows + the tight-card content.
 *
 * ①④ are always present and identical on every page. ②③⑤ are optional but render
 * identically wherever they appear — one shared style, never restyled per page.
 *
 * USAGE:
 * <PageLayout title="Page Title" description="Optional description">
 *   {children}
 * </PageLayout>
 *
 * With the optional zone slots (Pulse, Team, Billing):
 * <PageLayout
 *   title="Pulse"
 *   description="Your active assistants"
 *   actions={<Button>Action</Button>}
 *   intro={<PageIntroGuide label="How Pulse works">...</PageIntroGuide>}
 *   tabs={<TabsList>...</TabsList>}
 * >
 *   {children}
 * </PageLayout>
 */

import { ReactNode } from 'react';

interface PageLayoutProps {
  /** Page title displayed in the header (① TITLE) */
  title: string;
  /** Optional description below the title (② SUB-HEADING) */
  description?: string;
  /** Optional action buttons for the header (right side) */
  actions?: ReactNode;
  /** Optional intro-guide slot — always-visible tip rail below the subtitle (③) */
  intro?: ReactNode;
  /** Optional tab bar slot — rendered inside the header zone, below the rule (⑤)
   *  unless `tabsPlacement="header-right"` (Pulse HQ: title left, tabs top-right). */
  tabs?: ReactNode;
  /**
   * Where the tab bar sits. `below` (default) is slot ⑤ under the hairline.
   * `header-right` puts tabs on the title row — pulse.html `.page-head`.
   */
  tabsPlacement?: 'below' | 'header-right';
  /** Page content */
  children: ReactNode;
  /** Max width variant: 'default' (7xl/1280px) or 'narrow' (6xl/1152px) */
  maxWidth?: 'default' | 'narrow';
  /**
   * Compact header variant — reuses the `.page-title-compact` token (17px/400)
   * instead of the default 28px hero title, with a slimmer subtitle. For dense
   * catalog/list pages (Assistants) that need the header zone's rule/tabs slots
   * but not a hero title. Billing/Usage use the raw token outside PageLayout
   * entirely; this is the PageLayout-integrated version of the same idiom.
   */
  compact?: boolean;
  /** Optional className for the outer container */
  className?: string;
  /**
   * Skip ①–④ (title, sub, rule). IR3 puts Brain + subtitle in the left
   * column of the work split so Teach can sit top-right. One title only.
   */
  hideHeader?: boolean;
}

export function PageLayout({
  title,
  description,
  actions,
  intro,
  tabs,
  tabsPlacement = 'below',
  children,
  maxWidth = 'default',
  compact = false,
  className = '',
  hideHeader = false,
}: PageLayoutProps) {
  const maxWidthClass = maxWidth === 'narrow' ? 'max-w-6xl' : 'max-w-7xl';
  const hasActions = !!actions;
  const tabsInHeader = tabsPlacement === 'header-right' && !!tabs;

  if (hideHeader) {
    return (
      <div className={`page-container ${className}`}>
        <div className={`${maxWidthClass} mx-auto`}>
          <div className="animate-entrance">{children}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${className}`}>
      <div className={`${maxWidthClass} mx-auto`}>
        {/* Tier-ordered entrance (art-direction spec §A5): header first, the
            rest of the header zone +60ms, content +120ms. Same
            `.animate-entrance` idiom as the Assistants featured tile; the global
            reduced-motion guard lands every tier instantly. */}
        {/* ① TITLE + ② SUB-HEADING + actions / header-right tabs */}
        <div className={`page-header animate-entrance ${hasActions || tabsInHeader ? 'page-header--with-actions' : ''} ${tabsInHeader ? 'page-header--with-tabs' : ''} ${compact ? 'page-header--compact' : ''}`}>
          <div className="page-header__content">
            <h1>{title}</h1>
            {description && <p>{description}</p>}
          </div>
          {hasActions && (
            <div className="page-header__actions">
              {actions}
            </div>
          )}
          {tabsInHeader && (
            <div className="page-header__tabs" data-testid="page-header-tabs">
              {tabs}
            </div>
          )}
        </div>

        {/* ③ INTRO GUIDE (optional) */}
        {intro && <div className="mb-2 animate-entrance" style={{ animationDelay: '60ms' }}>{intro}</div>}

        {/* ④ HEADER HAIRLINE — always closes the header zone */}
        <div aria-hidden className="page-header-rule animate-entrance" style={{ animationDelay: '60ms' }} />

        {/* ⑤ TAB BAR (optional) — skipped when tabs already sit on the title row */}
        {tabs && !tabsInHeader && <div className="mt-3 animate-entrance" style={{ animationDelay: '60ms' }}>{tabs}</div>}

        {/* Page Content — single top gap after whichever of rule/tabs renders last */}
        <div className="mt-4 sm:mt-5 animate-entrance" style={{ animationDelay: '120ms' }}>{children}</div>
      </div>
    </div>
  );
}

export default PageLayout;
